import { InfoPopover, Stack } from 'component-library';
import { FC, HTMLAttributes, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { TickItem } from 'recharts-overriden/types/util/types';

import { PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { ProtectedAreas, reverseProtectedAreas } from '@/api/rest/resources/types/protectedAreas';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { Dot } from '@/components/Charts/components/Dot';
import { colorPalette } from '@/theme/colorPalette';
import { Area } from '@/utils/area';
import { isNullish } from '@/utils/booleans';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { Bar, BarChart, Chart, TooltipProps } from './components/Chart';
import { ChartTooltip, ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { NoChartData } from './NoChartData';
import { DefaultChartProps } from './types';
import { minMaxAxisDomain } from './utils';

const CHART_COLORS = {
  protectedAreaCategory: getColorForIndicator(INDICATOR.LANDSCAPE_PROTECTED_AREAS_OSM).fill,
} as const;
const UNIT = UnitEnum['km^2'] as const;
const RECTANGLE_RADIUS = 4 as const;

export const ProtectedAreasChart: FC<
  HTMLAttributes<HTMLDivElement> & DefaultChartProps & { data: PlotReportFactElement[] }
> = ({ data, ...delegated }) => {
  const { t } = useTranslation();

  const chartData = useGetChartData(data);

  if (!chartData || chartData.length === 0) {
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xAxisProps = {
    dataKey: 'label',
    type: 'category' as const,
  };

  return (
    <>
      <Chart.Container
        {...delegated}
        data={chartData}
        config={{
          protectedAreaCategory: {
            label: t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas'),
            color: CHART_COLORS.protectedAreaCategory,
          },
        }}
      >
        <BarChart data={chartData}>
          <Bar
            isAnimationActive={false}
            dataKey='protectedAreaCategory'
            fill={CHART_COLORS.protectedAreaCategory}
            activeBar={(props: unknown) => <CustomActiveBar {...(props as ActiveBarProps)} />}
            radius={[RECTANGLE_RADIUS, RECTANGLE_RADIUS, 0, 0]}
            className='protected-areas-bar'
          />
          <Chart.XAxis
            {...xAxisProps}
            padding={{ left: 10 }}
            tick={<CustomTick />}
            className='protected-areas-x-axis'
          />
          <Chart.YAxis domain={minMaxAxisDomain(0.8, 1.2)} className='protected-areas-y-axis'>
            <Chart.Label>{UNIT}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={<CustomTooltip />} cursor={false} />
        </BarChart>
      </Chart.Container>
    </>
  );
};

const useGetChartData = (data: PlotReportFactElement[]) => {
  const { t } = useTranslation();

  return data.flatMap((entry) => {
    const protectionCategory = entry.labels?.find(
      (label) => label.name === PlotReportFactLabelNameEnum.protection_category,
    )?.value as keyof typeof reverseProtectedAreas | undefined;

    if (!protectionCategory || typeof entry.value !== 'number') {
      return [];
    }

    const category = reverseProtectedAreas[`${protectionCategory}`];
    const protectedAreaCategory = parseFloat(
      new Area({
        value: entry.value,
        dimension: 'squareMeters',
      })
        .toSquareKilometers()
        .value.toFixed(2),
    );

    return {
      label: t(`global.protectedAreas.categories.${category}`),
      protectedAreaCategory,
      category,
    };
  });
};

type ActiveBarProps = SVGProps<SVGElement> & { background: { y: number } };

const CustomActiveBar = ({ x, y, width, height, background }: ActiveBarProps) => {
  if (!x || !y || !width || !height) return <></>;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);
  const barTop = background.y;

  const dotProps = {
    cx: barMiddleX,
    cy: y as number,
    fill: '#000000',
    strokeWidth: 0,
    opacity: 1,
  };

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={CHART_COLORS.protectedAreaCategory} ry={RECTANGLE_RADIUS} />
      <line x1={barMiddleX} x2={barMiddleX} y1={barTop} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
      <Dot.Container {...dotProps}>
        <Dot.Active {...dotProps} fill='#000000' />
      </Dot.Container>
    </g>
  );
};

const CustomTick = ({ x, y, payload }: SVGProps<SVGElement> & { payload?: TickItem }) => {
  const { t } = useTranslation();

  if (!payload) return null;

  const iucnCategoryValue = payload.value as (typeof ProtectedAreas)[keyof typeof ProtectedAreas];
  const iucnCategoryKey = Object.keys(ProtectedAreas).find(
    (key) => t(`global.protectedAreas.categories.${key as keyof typeof ProtectedAreas}`) === iucnCategoryValue,
  ) as keyof typeof ProtectedAreas;

  const iucnCategoryLabel = t(`global.protectedAreas.categories.${iucnCategoryKey}`);

  return (
    <foreignObject width='100%' height='100%' transform={`translate(${x},${y})`} style={{ overflow: 'visible' }}>
      <div className='w-fit h-fit'>
        <Stack direction='row' spacing={1} centerMain className='-translate-x-1/2 -translate-y-1/2 -rotate-45 my-1'>
          <span className='text-[12px] text-text-secondary'>{iucnCategoryValue}</span>
          <InfoPopover
            title={`${t('shared.ncaDetail.details.protectedAreas.graph.category', {
              category: iucnCategoryLabel,
            })}`}
            body={t(`global.protectedAreas.description.${iucnCategoryKey}`)}
          />
        </Stack>
      </div>
    </foreignObject>
  );
};

const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active) return null;

  const protectedAreaCategoryValue = payload?.find((data) => data.dataKey === 'protectedAreaCategory');

  const protectedAreaCategoryDisplay = !isNullish(protectedAreaCategoryValue?.payload?.protectedAreaCategory)
    ? `${protectedAreaCategoryValue?.payload?.protectedAreaCategory} ${UNIT}`
    : t('global.analysis.noData');

  const category = protectedAreaCategoryValue?.payload?.category as keyof typeof ProtectedAreas;

  const categoryAbbreviation = t(`global.protectedAreas.categories.${category}`);

  const categoryLabel = t('shared.ncaDetail.details.protectedAreas.graph.category', {
    category: categoryAbbreviation,
  });

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        {category && (
          <ChartTooltipTextSecondary>{t(`global.protectedAreas.description.${category}`)}</ChartTooltipTextSecondary>
        )}
      </Stack>
      <Stack spacing={8} direction='row' className='w-full justify-between'>
        <ChartTooltipTextPrimary>{categoryLabel}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{protectedAreaCategoryDisplay}</ChartTooltipTextPrimary>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as ProtectedAreasCustomTooltip };
